import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { isAuthenticated, logoutUser, getStoredUser, cleanupInvalidData } from "../api/auth";
import { getCurrentUser } from "../api/users";
import NotificationBell from "./NotificationBell";
import "./NavBar.css";

export default function NavBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState("");
  const [userRole, setUserRole] = useState("");
  const [profileCompletion, setProfileCompletion] = useState(100);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const checkAuth = async () => {
      // Clean up any invalid data first
      cleanupInvalidData();
      
      const loggedIn = isAuthenticated();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        const profile = getStoredUser();
        const user = profile?.user || profile;
        const firstName = user?.name?.split(' ')[0] || "User";
        setUserName(firstName);
        setUserRole(user?.role || "");
        
        // Fetch full profile to calculate completion
        try {
          const fullProfile = await getCurrentUser();
          const completion = calculateProfileCompletion(fullProfile);
          setProfileCompletion(completion);
        } catch (error) {
          console.error("Error fetching profile:", error);
        }
      }
    };
    checkAuth();
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate profile completion percentage
  const calculateProfileCompletion = (profileData) => {
    const user = profileData?.user || profileData;
    const isPartner = user?.role === "partner";
    const agencyDetails = profileData?.agency_profile;

    if (!user) return 0;

    const requiredFields = {
      basic: ['name', 'email', 'phone', 'current_location'],
      partner: ['age', 'gender', 'bio'],
      agency: ['agency_name', 'location', 'description', 'services']
    };

    let totalFields = requiredFields.basic.length;
    let filledFields = 0;

    // Check basic fields
    requiredFields.basic.forEach(field => {
      if (user[field]) filledFields++;
    });

    // Check role-specific fields
    if (isPartner) {
      // Partner fields (age, gender, bio) are at user level
      totalFields += requiredFields.partner.length;
      requiredFields.partner.forEach(field => {
        if (user[field]) filledFields++;
      });
    } else {
      // Agency fields are in agency_profile
      totalFields += requiredFields.agency.length;
      requiredFields.agency.forEach(field => {
        if (agencyDetails?.[field]) filledFields++;
      });
    }

    return Math.round((filledFields / totalFields) * 100);
  };

  const handleProfileClick = () => {
    setIsMenuOpen(false);
    setIsProfileDropdownOpen(false);
    navigate("/profile");
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleLogout = () => {
    logoutUser();
    setIsLoggedIn(false);
    setIsMenuOpen(false);
    navigate("/");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <span className="logo-icon">🌍</span>
          <span className="logo-text">Go With Me Buddy</span>
        </Link>

        <button 
          className={`mobile-menu-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <ul className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
          <li className="nav-item">
            <Link 
              to="/" 
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
          </li>
          
          {/* Show My Trips only for partners */}
          {isLoggedIn && userRole === "partner" && (
            <li className="nav-item">
              <Link 
                to="/my-trips" 
                className={`nav-link ${isActive('/my-trips') ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                My Trips
              </Link>
            </li>
          )}
          
          <li className="nav-item">
            <Link 
              to="/find-partner" 
              className={`nav-link ${isActive('/find-partner') ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
            >
              Find Partner
            </Link>
          </li>
          
          {/* Hide Agencies page for agency users - they only need Find Partner */}
          {userRole !== "agency" && (
            <li className="nav-item">
              <Link 
                to="/agencies" 
                className={`nav-link ${isActive('/agencies') ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                Agencies
              </Link>
            </li>
          )}
          {!isLoggedIn ? (
            <>
              <li className="nav-item nav-item-button">
                <Link 
                  to="/login" 
                  className="nav-link-btn login-btn"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
              </li>
              <li className="nav-item nav-item-button">
                <Link 
                  to="/register" 
                  className="nav-link-btn register-btn"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </li>
            </>
          ) : (
            <>
              <li className="nav-item nav-user" ref={dropdownRef}>
                <button 
                  className="nav-username" 
                  onClick={toggleProfileDropdown}
                >
                  <span className="user-icon">👤</span>
                  <span className="user-name">{userName}</span>
                  {profileCompletion < 100 && (
                    <span className="profile-completion-badge">
                      {profileCompletion}%
                    </span>
                  )}
                  <span className={`dropdown-arrow ${isProfileDropdownOpen ? 'open' : ''}`}>▼</span>
                </button>
                
                {isProfileDropdownOpen && (
                  <div className="profile-dropdown" style={{ zIndex: 1300 }}>
                    {profileCompletion < 100 ? (
                      <>
                        <div className="dropdown-header">
                          <div className="completion-info">
                            <span className="completion-text">Profile Completion</span>
                            <div className="progress-bar">
                              <div 
                                className="progress-fill" 
                                style={{ width: `${profileCompletion}%` }}
                              ></div>
                            </div>
                            <span className="completion-percentage">{profileCompletion}% Complete</span>
                          </div>
                        </div>
                        <button 
                          className="dropdown-item complete-profile-btn"
                          onClick={handleProfileClick}
                        >
                          <span className="item-icon">✨</span>
                          Complete Profile
                        </button>
                      </>
                    ) : (
                      <button 
                        className="dropdown-item"
                        onClick={handleProfileClick}
                      >
                        <span className="item-icon">👤</span>
                        My Profile
                      </button>
                    )}
                    <button 
                      className="dropdown-item logout-item"
                      onClick={handleLogout}
                    >
                      <span className="item-icon">🚪</span>
                      Logout
                    </button>
                  </div>
                )}
              </li>
            </>
          )}
        </ul>

        {/* Notification bell should be visible even when mobile menu is closed */}
        {isLoggedIn && (
          <div className="navbar-actions">
            <NotificationBell />
          </div>
        )}
      </div>
    </nav>
  );
}